import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPatch, getAuthHeaders } from '../../lib/api'
import { 
  ArrowLeft, 
  CheckCircle, 
  Clock, 
  BookOpen,
  AlertCircle,
  Sparkles,
  Loader2,
  Plus,
  MinusCircle,
  X,
  Link as LinkIcon,
  Unlink,
  Calendar,
  ChevronDown,
  Download,
  Printer
} from 'lucide-react'

interface Certification {
  id: string
  certName: string
  issuer: string | null
  issueDate: string | null
  expirationDate: string | null
}

interface CEProgress {
  userProfession: {
    id: string
    professionId: string
    professionName: string
    state: string
  }
  requirement: {
    id: string
    totalHours: number
    renewalPeriodYears: number
    notes: string | null
  } | null
  categories: Array<{
    id: string
    name: string
    categoryType: string
    requiredHours: number | null
    isOneTime: boolean
    isPediatricContent: boolean
    hoursEarned: number
    isCompleted: boolean
  }>
  totalHoursEarned: number
  percentComplete: number
  renewalCycle?: {
    startDate: string | null
    endDate: string | null
    linkedCertificationName: string | null
  }
}

interface AiLookupResult {
  totalHours: number | null
  renewalPeriodYears: number | null
  categories: CeCategory[]
  specialRequirements: string
  confidence: number
}

interface CeCategory {
  name: string
  requiredHours: number
  isPediatricContent?: boolean
  isOneTime?: boolean
}

interface CERecordItem {
  id: string
  title: string
  provider: string | null
  hoursAwarded: string | number | null
  dateCompleted: string | null
  documentUri?: string | null
  source: string
  notes: string | null
  allocations?: { userProfessionId: string }[]
}

export default function CEProgressPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()

  const [showSuggestModal, setShowSuggestModal] = useState(false)
  const [suggestStep, setSuggestStep] = useState<'loading' | 'review'>('loading')
  const [aiResult, setAiResult] = useState<AiLookupResult | null>(null)
  const [editedTotalHours, setEditedTotalHours] = useState('')
  const [editedRenewalYears, setEditedRenewalYears] = useState('2')
  const [editedSpecialRequirements, setEditedSpecialRequirements] = useState('')
  const [editedCategories, setEditedCategories] = useState<CeCategory[]>([])
  const [suggestError, setSuggestError] = useState('')
  const [showLinkDropdown, setShowLinkDropdown] = useState(false)
  const [selectionMode, setSelectionMode] = useState<'export' | 'print' | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isPreparing, setIsPreparing] = useState(false)

  const { data: progress, isLoading, error } = useQuery({
    queryKey: ['/api/user/professions', id, 'ce-progress'],
    queryFn: () => apiGet<CEProgress>(`/user/professions/${id}/ce-progress`),
    enabled: !!id,
  })

  const { data: certsData } = useQuery({
    queryKey: ['/api/certifications'],
    queryFn: () => apiGet<{ certifications: Certification[] }>('/certifications'),
    enabled: showLinkDropdown,
  })

  const linkCertMutation = useMutation({
    mutationFn: (linkedCertificationId: string | null) =>
      apiPatch(`/user/professions/${id}`, { linkedCertificationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/professions', id, 'ce-progress'] })
      queryClient.invalidateQueries({ queryKey: ['/api/user/professions'] })
      setShowLinkDropdown(false)
    },
  })

  const activeCerts = (certsData?.certifications || []).filter(c => c.expirationDate)

  const { data: ceRecordsData } = useQuery({
    queryKey: ['/api/continuing-education'],
    queryFn: () => apiGet<CERecordItem[]>('/continuing-education'),
  })

  const professionCERecords = (ceRecordsData || [])
    .filter(r => r.allocations?.some(a => a.userProfessionId === id))
    .sort((a, b) => (a.title || '').localeCompare(b.title || ''))

  const openSelectionModal = (mode: 'export' | 'print') => {
    setSelectedIds(new Set(professionCERecords.map(c => c.id)))
    setSelectionMode(mode)
  }

  const toggleCESelection = (recordId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(recordId)) next.delete(recordId)
      else next.add(recordId)
      return next
    })
  }

  const toggleCESelectAll = () => {
    if (selectedIds.size === professionCERecords.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(professionCERecords.map(c => c.id)))
    }
  }

  const getSelectedCERecords = () => professionCERecords.filter(c => selectedIds.has(c.id))

  const handleExportCECSV = () => {
    const selected = getSelectedCERecords()
    const profName = progress?.userProfession?.professionName || 'renewal'
    const rows = [
      ['Title', 'Provider', 'Hours Awarded', 'Date Completed', 'Source', 'Notes'],
      ...selected.map(record => [
        record.title,
        record.provider || '',
        String(record.hoursAwarded || 0),
        record.dateCompleted ? new Date(record.dateCompleted).toLocaleDateString() : '',
        record.source || '',
        record.notes || ''
      ])
    ]
    const csvContent = rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ce-${profName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
    setSelectionMode(null)
  }

  const handlePrintCE = async () => {
    setIsPreparing(true)
    const selected = getSelectedCERecords()
    const profName = progress?.userProfession?.professionName || 'Renewal'
    const profState = progress?.userProfession?.state || ''
    const imagePromises = selected.map(async (record) => {
      if (!record.documentUri) return { id: record.id, dataUrl: null }
      try {
        const res = await fetch(record.documentUri, { headers: getAuthHeaders(), credentials: 'include' })
        if (!res.ok) return { id: record.id, dataUrl: null }
        const blob = await res.blob()
        return new Promise<{ id: string; dataUrl: string | null }>((resolve) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve({ id: record.id, dataUrl: reader.result as string })
          reader.onerror = () => resolve({ id: record.id, dataUrl: null })
          reader.readAsDataURL(blob)
        })
      } catch {
        return { id: record.id, dataUrl: null }
      }
    })
    const imageResults = await Promise.all(imagePromises)
    const imageMap = new Map(imageResults.map(r => [r.id, r.dataUrl]))
    const recordCards = selected.map(record => {
      const img = imageMap.get(record.id)
      const dataFields = `
        <div class="record-data">
          <h2>${record.title}</h2>
          <div class="field"><span class="label">Provider</span><span class="value">${record.provider || '—'}</span></div>
          <div class="field"><span class="label">Hours</span><span class="value">${record.hoursAwarded || 0}</span></div>
          <div class="field"><span class="label">Date Completed</span><span class="value">${record.dateCompleted ? new Date(record.dateCompleted).toLocaleDateString() : '—'}</span></div>
          <div class="field"><span class="label">Source</span><span class="value">${record.source || '—'}</span></div>
          ${record.notes ? `<div class="field"><span class="label">Notes</span><span class="value">${record.notes}</span></div>` : ''}
        </div>
      `
      if (img) {
        return `<div class="record-card has-image"><div class="record-image"><img src="${img}" alt="${record.title}" /></div>${dataFields}</div>`
      }
      return `<div class="record-card no-image">${dataFields}</div>`
    }).join('')
    const printContent = `
      <html>
        <head>
          <title>CE Records — ${profName}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; color: #111; }
            h1 { font-size: 22px; margin-bottom: 4px; }
            .subtitle { color: #666; font-size: 13px; margin-bottom: 24px; }
            .record-card { border: 1px solid #e5e7eb; border-radius: 12px; margin-bottom: 20px; overflow: hidden; page-break-inside: avoid; }
            .record-card.has-image { display: flex; }
            .record-image { width: 50%; flex-shrink: 0; background: #f9fafb; display: flex; align-items: center; justify-content: center; padding: 16px; }
            .record-image img { max-width: 100%; max-height: 400px; object-fit: contain; border-radius: 6px; }
            .record-card.has-image .record-data { width: 50%; }
            .record-card.no-image .record-data { width: 100%; }
            .record-data { padding: 20px; display: flex; flex-direction: column; justify-content: center; gap: 10px; }
            .record-data h2 { font-size: 17px; font-weight: 700; color: #111; margin-bottom: 4px; }
            .field { display: flex; justify-content: space-between; align-items: center; }
            .label { font-size: 13px; color: #6b7280; }
            .value { font-size: 13px; font-weight: 500; color: #111; }
            @media print { body { padding: 0; } .record-card { break-inside: avoid; } }
          </style>
        </head>
        <body>
          <h1>CE Records — ${profName}${profState ? ` (${profState})` : ''}</h1>
          <p class="subtitle">Exported on ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} &bull; ${selected.length} record${selected.length !== 1 ? 's' : ''}</p>
          ${recordCards}
        </body>
      </html>
    `
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(printContent)
      printWindow.document.close()
      setTimeout(() => { printWindow.focus(); printWindow.print() }, 500)
    }
    setIsPreparing(false)
    setSelectionMode(null)
  }

  const aiLookupMutation = useMutation({
    mutationFn: (data: { professionName: string; state: string }) =>
      apiPost<AiLookupResult>('/professions/ai-lookup', data),
    onSuccess: (data) => {
      setAiResult(data)
      setEditedTotalHours(data.totalHours?.toString() || '')
      setEditedRenewalYears(data.renewalPeriodYears?.toString() || '2')
      setEditedSpecialRequirements(data.specialRequirements || '')
      setEditedCategories(data.categories || [])
      setSuggestStep('review')
    },
    onError: () => {
      setAiResult({ totalHours: null, renewalPeriodYears: 2, categories: [], specialRequirements: '', confidence: 0 })
      setEditedTotalHours('')
      setEditedRenewalYears('2')
      setEditedSpecialRequirements('')
      setEditedCategories([])
      setSuggestStep('review')
    },
  })

  const submitSuggestionMutation = useMutation({
    mutationFn: (data: {
      userProfessionId: string
      totalHours: number | null
      renewalPeriodYears: number
      ceCategories: CeCategory[]
      specialRequirements: string
      aiConfidence: number
    }) => apiPost('/professions/suggest-ce-requirements', data),
    onSuccess: () => {
      closeSuggestModal()
      window.alert('CE requirements submitted successfully. Thank you!')
    },
    onError: (err) => {
      setSuggestError(err instanceof Error ? err.message : 'Failed to submit suggestions')
    },
  })

  const closeSuggestModal = () => {
    setShowSuggestModal(false)
    setSuggestStep('loading')
    setAiResult(null)
    setEditedTotalHours('')
    setEditedRenewalYears('2')
    setEditedSpecialRequirements('')
    setEditedCategories([])
    setSuggestError('')
  }

  const handleStartSuggest = () => {
    if (!progress) return
    setShowSuggestModal(true)
    setSuggestStep('loading')
    aiLookupMutation.mutate({
      professionName: progress.userProfession.professionName,
      state: progress.userProfession.state,
    })
  }

  const handleSubmitSuggestion = () => {
    if (!id) return
    submitSuggestionMutation.mutate({
      userProfessionId: id,
      totalHours: editedTotalHours ? parseFloat(editedTotalHours) : null,
      renewalPeriodYears: parseInt(editedRenewalYears) || 2,
      ceCategories: editedCategories,
      specialRequirements: editedSpecialRequirements,
      aiConfidence: aiResult?.confidence || 0,
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error || !progress) {
    return (
      <div className="space-y-6">
        <Link to="/ce" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-5 h-5" />
          Back to CE
        </Link>
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <AlertCircle className="w-12 h-12 text-red-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Progress</h3>
          <p className="text-gray-500">Unable to load CE progress for this renewal.</p>
        </div>
      </div>
    )
  }

  const { userProfession, requirement, categories, totalHoursEarned, percentComplete } = progress
  const totalRequired = requirement?.totalHours || 0
  const renewalCycle = progress.renewalCycle

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return null
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const getTimeUntilExpiration = (dateString: string | null | undefined) => {
    if (!dateString) return { text: '', color: '' }
    const now = new Date()
    const expDate = new Date(dateString)
    const diffMs = expDate.getTime() - now.getTime()
    const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMs < 0) {
      const pastMs = Math.abs(diffMs)
      const days = Math.floor(pastMs / (1000 * 60 * 60 * 24))
      let text = ''
      if (days < 30) text = `${days} day${days !== 1 ? 's' : ''} ago`
      else {
        const months = Math.floor(days / 30)
        if (months < 12) text = `${months} month${months !== 1 ? 's' : ''} ago`
        else {
          const years = Math.floor(months / 12)
          text = `${years} year${years !== 1 ? 's' : ''} ago`
        }
      }
      return { text: `Expired ${text}`, color: 'text-red-600' }
    }

    const years = Math.floor(totalDays / 365)
    const remainingDaysAfterYears = totalDays % 365
    const months = Math.floor(remainingDaysAfterYears / 30)
    const days = remainingDaysAfterYears % 30

    const parts: string[] = []
    if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`)
    if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`)
    if (days > 0 && years === 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`)

    const text = parts.length > 0 ? `Expires in ${parts.join(' ')}` : 'Expires today'
    let color = 'text-green-600'
    if (totalDays < 30) color = 'text-red-600'
    else if (totalDays <= 90) color = 'text-yellow-600'

    return { text, color }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/ce" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{userProfession.professionName}</h1>
          <p className="text-gray-500">{userProfession.state}</p>
        </div>
      </div>

      {/* Linked Certification / Renewal Period */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
              <LinkIcon className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900">Renewal Period</h3>
              {renewalCycle?.linkedCertificationName ? (
                <p className="text-xs text-gray-500">
                  Linked to {renewalCycle.linkedCertificationName}
                </p>
              ) : (
                <p className="text-xs text-gray-500">
                  Link a certification to track your renewal dates
                </p>
              )}
            </div>
          </div>
          <div className="relative">
            {renewalCycle?.linkedCertificationName ? (
              <button
                onClick={() => linkCertMutation.mutate(null)}
                disabled={linkCertMutation.isPending}
                className="text-xs px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1"
              >
                <Unlink className="w-3 h-3" />
                Unlink
              </button>
            ) : (
              <button
                onClick={() => setShowLinkDropdown(!showLinkDropdown)}
                className="text-xs px-3 py-1.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-lg transition-colors flex items-center gap-1"
              >
                <LinkIcon className="w-3 h-3" />
                Link Certification
                <ChevronDown className="w-3 h-3" />
              </button>
            )}
            {showLinkDropdown && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-72 max-h-60 overflow-y-auto">
                {activeCerts.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500 text-center">
                    No certifications with expiration dates found
                  </div>
                ) : (
                  activeCerts.map(cert => (
                    <button
                      key={cert.id}
                      onClick={() => linkCertMutation.mutate(cert.id)}
                      disabled={linkCertMutation.isPending}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      <div className="text-sm font-medium text-gray-900">{cert.certName}</div>
                      <div className="text-xs text-gray-500">
                        {cert.issuer ? `${cert.issuer} · ` : ''}
                        Expires {formatDate(cert.expirationDate)}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
        {(renewalCycle?.startDate || renewalCycle?.endDate) && (() => {
          const expInfo = getTimeUntilExpiration(renewalCycle.endDate)
          return (
            <div className="mt-3 flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1.5 text-gray-600">
                <Calendar className="w-3.5 h-3.5" />
                <span>
                  {renewalCycle.startDate && renewalCycle.endDate
                    ? `${formatDate(renewalCycle.startDate)} — ${formatDate(renewalCycle.endDate)}`
                    : renewalCycle.endDate
                      ? `Expires ${formatDate(renewalCycle.endDate)}`
                      : `Started ${formatDate(renewalCycle.startDate)}`}
                </span>
              </div>
              {expInfo.text && (
                <span className={`font-medium ${expInfo.color}`}>
                  {expInfo.text}
                </span>
              )}
            </div>
          )
        })()}
      </div>

      {!requirement ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-amber-900">CE Requirements Not Yet Configured</h3>
              <p className="text-sm text-amber-700 mt-1">
                The CE requirements for {userProfession.professionName} in {userProfession.state} haven't been set up yet. 
                You can still track your CE records, and they'll count toward your progress once requirements are configured.
              </p>
              <button
                onClick={handleStartSuggest}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                Help Set Up Requirements
              </button>
              <p className="text-xs text-amber-600 mt-2">
                Help us set up the CE requirements for your renewal and state.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Overall Progress</h2>
              {percentComplete >= 100 ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                  <CheckCircle className="w-4 h-4" />
                  Complete
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  <Clock className="w-4 h-4" />
                  In Progress
                </span>
              )}
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-600">Hours Completed</span>
                <span className="font-semibold text-gray-900">
                  {totalHoursEarned.toFixed(1)} / {totalRequired} hours
                </span>
              </div>
              <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    percentComplete >= 100 ? 'bg-green-500' : percentComplete >= 75 ? 'bg-blue-500' : 'bg-yellow-500'
                  }`}
                  style={{ width: `${Math.min(percentComplete, 100)}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Renewal Period</p>
                <p className="font-medium text-gray-900">{requirement.renewalPeriodYears} years</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Hours Remaining</p>
                <p className="font-medium text-gray-900">
                  {Math.max(0, totalRequired - totalHoursEarned).toFixed(1)} hours
                </p>
              </div>
            </div>

            {requirement.notes && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">{requirement.notes}</p>
              </div>
            )}
          </div>

          {categories.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Category Requirements</h2>
              <div className="space-y-4">
                {categories.map((category) => {
                  const catPercent = category.requiredHours 
                    ? Math.min((category.hoursEarned / category.requiredHours) * 100, 100)
                    : category.isCompleted ? 100 : 0

                  return (
                    <div key={category.id} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{category.name}</span>
                          {category.isPediatricContent && (
                            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">Pediatric</span>
                          )}
                          {category.isOneTime && (
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">One-time</span>
                          )}
                        </div>
                        {category.isCompleted ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <span className="text-sm text-gray-500">
                            {category.requiredHours 
                              ? `${category.hoursEarned.toFixed(1)} / ${category.requiredHours} hrs`
                              : 'Not completed'
                            }
                          </span>
                        )}
                      </div>
                      {category.requiredHours && (
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${category.isCompleted ? 'bg-green-500' : 'bg-blue-500'}`}
                            style={{ width: `${catPercent}%` }}
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">CE Records</h2>
          <div className="flex items-center gap-2">
            {professionCERecords.length > 0 && (
              <>
                <button
                  onClick={() => openSelectionModal('export')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors"
                  data-testid="button-export-profession-ce"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export
                </button>
                <button
                  onClick={() => openSelectionModal('print')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors"
                  data-testid="button-print-profession-ce"
                >
                  <Printer className="w-3.5 h-3.5" />
                  Print
                </button>
              </>
            )}
            <Link 
              to="/ce/new?mode=upload"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Add New
            </Link>
          </div>
        </div>
        {professionCERecords.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p>CE records for this renewal will appear here.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {professionCERecords.map(record => (
              <Link
                key={record.id}
                to={`/ce/${record.id}`}
                className="flex items-center justify-between py-3 hover:bg-gray-50 -mx-2 px-2 rounded transition-colors"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{record.title}</p>
                  <p className="text-xs text-gray-500 truncate">{record.provider || 'Unknown provider'}</p>
                </div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 flex-shrink-0 ml-3">
                  {record.hoursAwarded || 0} hrs
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* CE Requirement Suggestion Modal */}
      {showSuggestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {suggestStep === 'loading' ? 'Finding Requirements...' : 'Confirm CE Requirements'}
              </h3>
              <button onClick={closeSuggestModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {suggestStep === 'loading' ? (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <Loader2 className="w-10 h-10 text-purple-600 animate-spin" />
                <p className="text-gray-600 text-center">
                  Finding CE requirements for<br />
                  <strong>{userProfession.professionName}</strong> in <strong>{userProfession.state}</strong>...
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {suggestError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <p className="text-sm text-red-600">{suggestError}</p>
                  </div>
                )}

                {aiResult && aiResult.confidence > 0 && (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                    aiResult.confidence >= 70 
                      ? 'bg-green-50 border border-green-200 text-green-700' 
                      : 'bg-yellow-50 border border-yellow-200 text-yellow-700'
                  }`}>
                    {aiResult.confidence >= 70 ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <AlertCircle className="w-4 h-4" />
                    )}
                    Accuracy: {aiResult.confidence}%
                  </div>
                )}

                <p className="text-sm text-gray-600">
                  Please verify and edit the CE requirements below before saving.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total CE Hours Required
                  </label>
                  <input
                    type="number"
                    value={editedTotalHours}
                    onChange={(e) => setEditedTotalHours(e.target.value)}
                    placeholder="e.g. 40"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Renewal Period (years)
                  </label>
                  <input
                    type="number"
                    value={editedRenewalYears}
                    onChange={(e) => setEditedRenewalYears(e.target.value)}
                    placeholder="e.g. 2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CE Categories
                  </label>
                  <div className="space-y-2">
                    {editedCategories.map((cat, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={cat.name}
                          onChange={(e) => {
                            const updated = [...editedCategories]
                            updated[idx] = { ...updated[idx], name: e.target.value }
                            setEditedCategories(updated)
                          }}
                          placeholder="Category name"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="number"
                          value={cat.requiredHours}
                          onChange={(e) => {
                            const updated = [...editedCategories]
                            updated[idx] = { ...updated[idx], requiredHours: parseFloat(e.target.value) || 0 }
                            setEditedCategories(updated)
                          }}
                          placeholder="Hours"
                          className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={() => setEditedCategories(editedCategories.filter((_, i) => i !== idx))}
                          className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                        >
                          <MinusCircle className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => setEditedCategories([...editedCategories, { name: '', requiredHours: 0 }])}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Add Category
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Special Requirements / Notes
                  </label>
                  <textarea
                    value={editedSpecialRequirements}
                    onChange={(e) => setEditedSpecialRequirements(e.target.value)}
                    rows={3}
                    placeholder="Any additional notes about CE requirements..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={closeSuggestModal}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitSuggestion}
                    disabled={submitSuggestionMutation.isPending}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitSuggestionMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Save Requirements'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {selectionMode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectionMode === 'export' ? 'Export CE Records' : 'Print CE Records'}
              </h3>
              <button onClick={() => setSelectionMode(null)} className="p-1 hover:bg-gray-100 rounded-lg" data-testid="button-close-ce-profession-modal">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="px-6 py-3 border-b border-gray-100">
              <p className="text-sm text-gray-500">
                Showing CE records allocated to {progress?.userProfession?.professionName || 'this renewal'}. Uncheck any you do not want to include.
              </p>
            </div>

            <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="checkbox-profession-ce-select-all">
                <input
                  type="checkbox"
                  checked={selectedIds.size === professionCERecords.length && professionCERecords.length > 0}
                  onChange={toggleCESelectAll}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  {selectedIds.size === professionCERecords.length ? 'Deselect All' : 'Select All'}
                </span>
              </label>
              <span className="text-sm text-gray-500">{selectedIds.size} of {professionCERecords.length} selected</span>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-2 divide-y divide-gray-100">
              {professionCERecords.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500">
                  No CE records found for this renewal.
                </div>
              ) : professionCERecords.map(record => (
                <label key={record.id} className="flex items-center gap-3 py-3 cursor-pointer" data-testid={`checkbox-profession-ce-${record.id}`}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(record.id)}
                    onChange={() => toggleCESelection(record.id)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{record.title}</p>
                    <p className="text-xs text-gray-500 truncate">{record.provider || 'Unknown provider'}</p>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 flex-shrink-0">
                    {record.hoursAwarded || 0} hrs
                  </span>
                </label>
              ))}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              {isPreparing && (
                <div className="flex items-center gap-2 text-sm text-gray-500 mr-auto">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Preparing...
                </div>
              )}
              <button
                onClick={() => setSelectionMode(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={selectionMode === 'export' ? handleExportCECSV : handlePrintCE}
                disabled={selectedIds.size === 0 || isPreparing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid={`button-confirm-${selectionMode}-profession-ce`}
              >
                {selectionMode === 'export' ? (
                  <><Download className="w-4 h-4" /> Export CSV</>
                ) : (
                  <><Printer className="w-4 h-4" /> Print</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
